import { useState, lazy, Suspense, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import { Button, Card, H3, H4, Body, BodySm } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { DocumentEditPanel } from '@/components/documents/DocumentEditPanel';
import { api } from '@/lib/api';
import { useAppStore } from '@/lib/store';
import { cn } from '@/lib/utils';
import type { DocumentType, DocumentContent, GeneratedDocument, DocumentTypeInfo } from '@/types';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { LegalDocumentPDF } from '@/components/pdf/LegalDocumentPDF';

const PDFViewer = lazy(() =>
  import('@react-pdf/renderer').then((mod) => ({ default: mod.PDFViewer }))
);

const DOCUMENT_TYPES: DocumentTypeInfo[] = [
  { id: 'demand_letter', label: 'Demand Letter', description: 'Formal demand for payment, action, or remedy', estimatedTokens: 300 },
  { id: 'cease_desist', label: 'Cease & Desist', description: 'Order someone to stop a harmful activity', estimatedTokens: 350 },
  { id: 'security_deposit_demand', label: 'Security Deposit Demand', description: 'Demand return of security deposit from landlord', estimatedTokens: 280 },
  { id: 'breach_of_contract', label: 'Breach of Contract', description: 'Notify a party of contract violation', estimatedTokens: 320 },
  { id: 'dmca_takedown', label: 'DMCA Takedown Notice', description: 'Request removal of infringing content', estimatedTokens: 300 },
  { id: 'notice_letter', label: 'General Notice Letter', description: 'Formal notice of intent or information', estimatedTokens: 250 },
  { id: 'nda', label: 'Non-Disclosure Agreement', description: 'Simple confidentiality agreement between parties', estimatedTokens: 400 },
  { id: 'contractor_agreement', label: 'Contractor Agreement', description: 'Basic freelance/contractor services agreement', estimatedTokens: 450 },
];

const LANDLORD_TENANT_TYPES: DocumentTypeInfo[] = [
  { id: 'eviction_response', label: 'Eviction Response', description: 'Tenant response to eviction or unlawful detainer notice', estimatedTokens: 320 },
  { id: 'repair_request', label: 'Repair Request Letter', description: 'Formal demand to landlord for property repairs', estimatedTokens: 280 },
  { id: 'lease_review_summary', label: 'Lease Review Summary', description: 'Plain-English analysis of a lease agreement', estimatedTokens: 350 },
  { id: 'habitability_complaint', label: 'Habitability Complaint', description: 'Formal complaint about uninhabitable living conditions', estimatedTokens: 300 },
];

type Step = 'select' | 'generating' | 'preview';

export function DocumentGeneratePage() {
  const [searchParams] = useSearchParams();
  const { session } = useClerk();
  const chatId = searchParams.get('chatId') || '';

  const [step, setStep] = useState<Step>('select');
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);
  const [generatedDoc, setGeneratedDoc] = useState<GeneratedDocument | null>(null);
  const [editedContent, setEditedContent] = useState<DocumentContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const addGeneratedDocument = useAppStore((s) => s.addGeneratedDocument);

  const handleGenerate = async () => {
    if (!selectedType || !chatId) return;
    setStep('generating');
    setError(null);

    try {
      if (session) {
        const token = await session.getToken();
        api.setAuthToken(token);
      }

      const result = await api.generateDocument(chatId, selectedType);
      setGeneratedDoc(result.document);
      setEditedContent(result.document.content);
      addGeneratedDocument(result.document);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate document');
      setStep('select');
    }
  };

  const handleContentChange = useCallback((content: DocumentContent) => {
    setEditedContent(content);
  }, []);

  const handleSave = async () => {
    if (!generatedDoc || !editedContent) return;
    try {
      await api.updateGeneratedDocument(generatedDoc.id, editedContent);
    } catch {
      // Non-critical - document is still usable
    }
  };

  const handleDownloaded = async () => {
    if (!generatedDoc) return;
    await api.markDocumentDownloaded(generatedDoc.id).catch(() => {});
  };

  if (!chatId) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <Card padding="lg" className="text-center max-w-sm">
          <H4 className="mb-2">No chat selected</H4>
          <Body className="text-surface-500 mb-4">Please start from a chat conversation.</Body>
          <Link to="/chat">
            <Button>Go to Chat</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-surface-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/chat?chatId=${chatId}`}>
            <Button variant="ghost" size="sm" icon={<NotLegalIcon name="arrowLeft" className="w-4 h-4" />}>
              Back to Chat
            </Button>
          </Link>
          <div className="h-5 w-px bg-surface-200" />
          <H3 className="text-lg">
            {step === 'select' && 'Generate Legal Document'}
            {step === 'generating' && 'Generating your document...'}
            {step === 'preview' && (generatedDoc?.title || 'Document Ready')}
          </H3>
        </div>
        {step === 'preview' && generatedDoc && editedContent && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleSave}>
              Save Edits
            </Button>
            <PDFDownloadLink
              document={<LegalDocumentPDF title={generatedDoc.title} documentType={generatedDoc.document_type} content={editedContent} />}
              fileName={`${generatedDoc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`}
            >
              {({ loading }) => (
                <Button
                  size="sm"
                  loading={loading}
                  icon={<NotLegalIcon name="fileDownload" className="w-4 h-4" />}
                  onClick={handleDownloaded}
                >
                  Download PDF
                </Button>
              )}
            </PDFDownloadLink>
          </div>
        )}
      </header>

      {/* Step 1: Type Selection */}
      {step === 'select' && (
        <div className="flex-1 p-6 max-w-4xl mx-auto w-full">
          <div className="mb-6">
            <Body className="text-surface-600">
              Choose the type of legal document to generate from your chat conversation.
            </Body>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {DOCUMENT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={cn(
                  'text-left p-5 rounded-xl border-2 transition-all duration-200',
                  selectedType === type.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-surface-200 bg-white hover:border-surface-300 hover:bg-surface-50'
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <H4 className="text-sm font-semibold">{type.label}</H4>
                  <span className="text-xs text-surface-400 bg-surface-100 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                    ~{type.estimatedTokens} tokens
                  </span>
                </div>
                <BodySm className="text-surface-500">{type.description}</BodySm>
              </button>
            ))}
          </div>

          <div className="mb-2">
            <BodySm className="font-semibold text-surface-600 flex items-center gap-2">
              <span>🏠</span> Landlord-Tenant
            </BodySm>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {LANDLORD_TENANT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={cn(
                  'text-left p-5 rounded-xl border-2 transition-all duration-200',
                  selectedType === type.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-surface-200 bg-white hover:border-surface-300 hover:bg-surface-50'
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <H4 className="text-sm font-semibold">{type.label}</H4>
                  <span className="text-xs text-surface-400 bg-surface-100 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                    ~{type.estimatedTokens} tokens
                  </span>
                </div>
                <BodySm className="text-surface-500">{type.description}</BodySm>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <BodySm className="text-surface-400 text-xs">
              Claude will analyze your chat to draft the document. You can edit it before downloading.
            </BodySm>
            <Button
              disabled={!selectedType}
              onClick={handleGenerate}
              icon={<NotLegalIcon name="document" className="w-4 h-4" />}
            >
              Generate Document
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Generating */}
      {step === 'generating' && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-6 animate-pulse">
              <NotLegalIcon name="document" className="w-10 h-10 text-primary-600" />
            </div>
            <H3 className="mb-3">Drafting your document...</H3>
            <Body className="text-surface-500 mb-2">
              Claude is analyzing your conversation and writing a professional{' '}
              <span className="font-medium text-surface-700">
                {[...DOCUMENT_TYPES, ...LANDLORD_TENANT_TYPES].find(t => t.id === selectedType)?.label}
              </span>
              .
            </Body>
            <BodySm className="text-surface-400">This usually takes 10-20 seconds.</BodySm>
          </div>
        </div>
      )}

      {/* Step 3: Preview + Edit */}
      {step === 'preview' && generatedDoc && editedContent && (
        <div className="flex-1 flex overflow-hidden">
          {/* Edit Panel */}
          <div className="w-2/5 border-r border-surface-200 bg-white flex flex-col">
            <div className="p-4 border-b border-surface-200 bg-surface-50">
              <BodySm className="font-semibold text-surface-700">Edit Document</BodySm>
              <BodySm className="text-surface-400 text-xs mt-0.5">Changes update the preview in real-time</BodySm>
            </div>
            <DocumentEditPanel content={editedContent} onChange={handleContentChange} />
          </div>

          {/* PDF Preview */}
          <div className="flex-1 flex flex-col bg-surface-100">
            <div className="p-4 border-b border-surface-200 bg-white flex items-center justify-between">
              <BodySm className="font-semibold text-surface-700">PDF Preview</BodySm>
              <button
                onClick={() => setShowPdfPreview(!showPdfPreview)}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                {showPdfPreview ? 'Hide Preview' : 'Show PDF Preview'}
              </button>
            </div>
            {showPdfPreview ? (
              <Suspense
                fallback={
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <BodySm className="text-surface-500">Loading PDF renderer...</BodySm>
                    </div>
                  </div>
                }
              >
                <div className="flex-1">
                  <PDFViewer width="100%" height="100%" showToolbar={false}>
                    <LegalDocumentPDF
                      title={generatedDoc.title}
                      documentType={generatedDoc.document_type}
                      content={editedContent}
                    />
                  </PDFViewer>
                </div>
              </Suspense>
            ) : (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-sm">
                  <div className="w-16 h-20 bg-white border-2 border-surface-300 rounded-lg mx-auto mb-4 flex items-center justify-center shadow-sm">
                    <NotLegalIcon name="document" className="w-8 h-8 text-surface-400" />
                  </div>
                  <H4 className="mb-2">{generatedDoc.title}</H4>
                  <Body className="text-surface-500 mb-4 text-sm">
                    Edit the document on the left, then click "Show PDF Preview" to see the formatted output, or download directly.
                  </Body>
                  <PDFDownloadLink
                    document={<LegalDocumentPDF title={generatedDoc.title} documentType={generatedDoc.document_type} content={editedContent} />}
                    fileName={`${generatedDoc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`}
                  >
                    {({ loading }) => (
                      <Button
                        loading={loading}
                        icon={<NotLegalIcon name="fileDownload" className="w-4 h-4" />}
                        onClick={handleDownloaded}
                      >
                        Download PDF
                      </Button>
                    )}
                  </PDFDownloadLink>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
